import { useQuery } from '@tanstack/react-query';
import { ChevronsUpDown } from 'lucide-react';
import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { fetchFactoryAreas, fetchProductionLinesByArea } from '@/core/api';
import { useFactory } from '@/core/hooks/useFactory';
import { useTenant } from '@/core/hooks/useTenant';
import { ScrapDatePicker } from '@/core/components/scrap/ScrapDatePicker';
import { AreaScrapSection } from '@/core/components/scrap/AreaScrapSection';
import { Card, CardContent } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { useLanguage } from '@/core/LanguageContext';

/**
 * Scrap data entry page - compact, efficient design with visual hierarchy.
 */
export const ScrapEntryPage = () => {
  const { selectedFactoryId } = useFactory();
  const { selectedTenantId } = useTenant();
  const { t } = useLanguage();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [expandedAreas, setExpandedAreas] = useState<Set<string>>(new Set());

  const entryDate = format(selectedDate, 'yyyy-MM-dd');

  const { data: areas = [], isLoading: areasLoading } = useQuery({
    queryKey: ['factory-areas', selectedTenantId, selectedFactoryId],
    queryFn: () => {
      if (!selectedFactoryId) return [];
      return fetchFactoryAreas(selectedFactoryId);
    },
    enabled: !!selectedFactoryId,
  });

  const { data: allLines = [], isLoading: linesLoading } = useQuery({
    queryKey: ['production-lines', selectedTenantId, selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId || areas.length === 0) return [];
      const results = await Promise.all(
        areas.map((area) =>
          fetchProductionLinesByArea(area.id).then((lines) => ({ areaId: area.id, lines }))
        )
      );
      return results.flatMap((result) =>
        result.lines.map((line) => ({ ...line, areaId: result.areaId }))
      );
    },
    enabled: !!selectedFactoryId && areas.length > 0,
  });

  // Areas that have lines
  const areasWithLines = useMemo(() => 
    areas.filter((a) => allLines.some((l) => l.area_id === a.id)),
    [areas, allLines]
  );

  const toggleArea = (areaId: string) => {
    setExpandedAreas((prev) => {
      const next = new Set(prev);
      if (next.has(areaId)) {
        next.delete(areaId);
      } else {
        next.add(areaId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (expandedAreas.size === areasWithLines.length) {
      // All expanded -> collapse all
      setExpandedAreas(new Set());
    } else {
      // Some or none expanded -> expand all
      setExpandedAreas(new Set(areasWithLines.map((a) => a.id)));
    }
  };

  const allExpanded = expandedAreas.size === areasWithLines.length && areasWithLines.length > 0;

  if (!selectedFactoryId) {
    return (
      <div className="space-y-4">
        <Alert>
          <AlertDescription>Please select a factory to view scrap entries.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const isLoading = areasLoading || linesLoading;

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground">
            {t('core.scrap.title', 'Scrap Entry')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('core.scrap.subtitle', 'Daily loss tracking per production line')}
          </p>
        </div>

        {/* Date picker - always visible in header */}
        <ScrapDatePicker selectedDate={selectedDate} onDateChange={setSelectedDate} />
      </div>

      {/* Date context + toggle all */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {t('core.scrap.viewing', 'Viewing')} <span className="font-medium text-foreground">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</span>
        </div>
        
        {!isLoading && areasWithLines.length > 0 && (
          <Button variant="ghost" size="sm" onClick={toggleAll} className="h-7 text-xs gap-1">
            <ChevronsUpDown className="h-3 w-3" />
            {allExpanded ? t('core.scrap.collapseAll', 'Collapse all') : t('core.scrap.expandAll', 'Expand all')}
          </Button>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <Card>
          <CardContent className="py-4 space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      )}

      {/* No data */}
      {!isLoading && areas.length === 0 && (
        <Alert>
          <AlertDescription>
            {t('core.scrap.noAreas', 'No production areas found. Configure your factory structure first.')}
          </AlertDescription>
        </Alert>
      )}

      {/* Areas & Lines - with visual hierarchy */}
      {!isLoading && areasWithLines.length > 0 && (
        <Card>
          <CardContent className="p-0">
            {areasWithLines.map((area) => {
              const linesInArea = allLines.filter((line) => line.area_id === area.id);
              return (
                <AreaScrapSection
                  key={area.id}
                  area={area}
                  lines={linesInArea}
                  entryDate={entryDate}
                  factoryId={selectedFactoryId}
                  isExpanded={expandedAreas.has(area.id)}
                  onToggle={() => toggleArea(area.id)}
                />
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
